import React from 'react';
import { Star, Flame, Footprints } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getTraitStyle } from '@/lib/traitMeta';

/**
 * Pill badge per singolo trait — icona + label, colore per categoria.
 */
export const TraitBadge = ({ traitKey, catalog = [], size = 'sm', className }) => {
  const entry = catalog.find((c) => c.key === traitKey);
  const { Icon, style } = getTraitStyle(entry || { key: traitKey });
  const label = entry?.label || traitKey;
  const sizeClasses = size === 'md'
    ? 'text-sm px-2.5 py-1 gap-1.5'
    : 'text-xs px-2 py-0.5 gap-1';
  const iconSize = size === 'md' ? 'h-3.5 w-3.5' : 'h-3 w-3';
  return (
    <span
      title={entry?.description || label}
      className={cn(
        'inline-flex items-center rounded-full border font-medium',
        sizeClasses,
        style.badge,
        className,
      )}
    >
      {Icon ? <Icon className={cn(iconSize, style.icon)} strokeWidth={2.2} /> : null}
      <span>{label}</span>
    </span>
  );
};

const FOOT_LABEL = { right: 'DX', left: 'SX', both: 'DX+SX' };
const FOOT_TITLE = { right: 'Piede destro', left: 'Piede sinistro', both: 'Ambidestro' };

/**
 * Piede preferito, compatto: DX / SX / DX+SX.
 */
export const FootBadge = ({ foot, size = 'sm', className }) => {
  const label = FOOT_LABEL[foot];
  if (!label) return null;
  return (
    <span
      title={FOOT_TITLE[foot]}
      data-testid="foot-badge"
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full border px-1.5 font-medium text-muted-foreground',
        size === 'md' ? 'text-xs py-0.5' : 'text-[10px] py-0',
        className,
      )}
    >
      <Footprints className={size === 'md' ? 'h-3 w-3' : 'h-2.5 w-2.5'} strokeWidth={2.2} />
      {label}
    </span>
  );
};

/**
 * "ultime 5: V V P S V · MVP 1" — testo del tooltip della forma.
 */
export const formBreakdownLabel = (breakdown) => {
  const recent = breakdown?.recent || [];
  if (!recent.length) return 'nessuna partita negli ultimi 60 giorni';
  const letters = recent.map((r) => ({ win: 'V', draw: 'P', loss: 'S' }[r.outcome] || '?')).join(' ');
  const mvp = recent.filter((r) => r.mvp_won).length;
  return `ultime ${recent.length}: ${letters}${mvp ? ` · MVP ${mvp}` : ''}`;
};

/**
 * Render compatto: skill stars + forma + piede + trait badges in linea.
 * La forma è mostrata accanto alle stelle ogni volta che il giocatore è valutato
 * (così è "subito visibile" come le stelle); se non impostata mostra la neutra (3).
 */
export const PlayerTraitsRow = ({
  traits = [], skillLevel, form, formBreakdown, preferredFoot, catalog = [], size = 'sm', showValues = true, className,
}) => {
  const hasFormValue = Number.isFinite(Number(form)) && Number(form) >= 1 && Number(form) <= 5;
  const showForm = !!skillLevel || hasFormValue;
  const formLevel = hasFormValue ? Number(form) : 3;
  if (!traits.length && !skillLevel && !hasFormValue && !preferredFoot) return null;
  const num = size === 'md' ? 'text-sm' : 'text-xs';
  return (
    <div className={cn('flex flex-wrap items-center gap-x-2.5 gap-y-1', className)}>
      {skillLevel ? (
        <span className="inline-flex items-center gap-1" title={`Livello ${Number(skillLevel)}/5`}>
          <SkillStars level={skillLevel} size={size} />
          {showValues && <span className={cn(num, 'font-semibold tabular-nums text-amber-600')} data-testid="skill-value">{Number(skillLevel).toFixed(1)}</span>}
        </span>
      ) : null}
      {showForm ? (
        <span className="inline-flex items-center gap-1" title={`Forma ${formLevel}/5 · ${formBreakdownLabel(formBreakdown)}`}>
          <FormMeter level={formLevel} breakdown={formBreakdown} size={size} />
          {showValues && <span className={cn(num, 'font-semibold tabular-nums', formLevel >= 4 ? 'text-orange-600' : formLevel <= 2 ? 'text-sky-600' : 'text-muted-foreground')} data-testid="form-value">{formLevel.toFixed(1)}</span>}
        </span>
      ) : null}
      <FootBadge foot={preferredFoot} size={size} />
      {traits.map((k) => (
        <TraitBadge key={k} traitKey={k} catalog={catalog} size={size} />
      ))}
    </div>
  );
};

/**
 * Una fila di icone 1..5 con mezzi passi: l'icona a metà è quella vuota con
 * sopra la piena tagliata al 50% (nessuna dipendenza, funziona con ogni icona).
 */
export const RatingIcons = ({ Icon, level, max, iconClass, fullClass, emptyClass, title, className, testId }) => {
  const n = Math.max(0, Math.min(max, Number(level) || 0));
  return (
    <span title={title} data-testid={testId} data-level={n} className={cn('inline-flex items-center gap-0.5', className)}>
      {Array.from({ length: max }).map((_, i) => {
        const full = n >= i + 1;
        const half = !full && n >= i + 0.5;
        return (
          <span
            key={i}
            className="relative inline-flex"
            data-full={full ? '' : undefined}
            data-half={half ? '' : undefined}
          >
            <Icon className={cn(iconClass, full ? fullClass : emptyClass)} strokeWidth={2} />
            {half && (
              <span className="absolute inset-y-0 left-0 w-1/2 overflow-hidden" aria-hidden="true">
                <Icon className={cn(iconClass, fullClass)} strokeWidth={2} />
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
};

/**
 * Skill 1-5 (mezze stelle) come stelle piene/vuote, con tooltip.
 */
export const SkillStars = ({ level = 0, max = 5, size = 'sm', className }) => {
  const n = Math.max(0, Math.min(max, Number(level) || 0));
  return (
    <RatingIcons
      Icon={Star}
      level={n}
      max={max}
      iconClass={size === 'md' ? 'h-4 w-4' : 'h-3 w-3'}
      fullClass="fill-amber-400 text-amber-400"
      emptyClass="fill-transparent text-muted-foreground/40"
      title={`Livello ${n}/${max}`}
      testId="skill-stars"
      className={className}
    />
  );
};

/**
 * Forma 1-5 (condizione attuale, mezzi passi) come fiammelle. Distinta dalle
 * stelle: arancione = in forma, azzurro = giù di forma. Il tooltip spiega da
 * quali partite viene.
 */
export const FormMeter = ({ level = 0, max = 5, breakdown, size = 'sm', className }) => {
  const n = Math.max(0, Math.min(max, Number(level) || 0));
  const tone = n >= 4 ? 'text-orange-500' : n <= 2 ? 'text-sky-500' : 'text-muted-foreground';
  const title = `Forma ${n}/${max}${breakdown ? ` · ${formBreakdownLabel(breakdown)}` : ''}`;
  return (
    <RatingIcons
      Icon={Flame}
      level={n}
      max={max}
      iconClass={size === 'md' ? 'h-4 w-4' : 'h-3 w-3'}
      fullClass={cn('fill-current', tone)}
      emptyClass="fill-transparent text-muted-foreground/30"
      title={title}
      testId="form-meter"
      className={className}
    />
  );
};

export default PlayerTraitsRow;
